import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isString from 'lodash/isString';
import { ButtonWithIcon } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import highlightMatch from '@Utils/highlightMatch';
import { TAXOMONY_STATES } from '@Services/TreeView/TreeViewConstants';

import './TaxonomyLeydenTree.scss';

// Prevent a propType warning from Leyden's Checkbox when label is a node.
// eslint-disable-next-line react/forbid-foreign-prop-types
Checkbox.propTypes.label = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);

const ID_PREFIX = 'tax';
const COUNT_UNIT_LABEL = 'hits';

/** Format a hit count as a localized integer string (rendered as text, not color-only). */
const formatCount = (hitCount) => {
  if (hitCount == null || hitCount === '') return null;
  const num = isString(hitCount) ? parseFloat(hitCount) : hitCount;
  if (Number.isNaN(num)) return hitCount;
  return Math.trunc(num).toLocaleString('en-US');
};

const toNumber = (hitCount) => {
  const num = isString(hitCount) ? parseFloat(hitCount) : hitCount;
  return Number.isFinite(num) ? num : 0;
};

const getNodeKey = (node) => String(node?.id ?? node?.name ?? '');

const hasNestedChildren = (node) => Boolean(node?.isFolder || node?.children?.length || node?.hasChildren || node?.childrenCount > 0);

const getRootTaxonomies = (taxonomies) => {
  const childIds = new Set();
  taxonomies.forEach((node) => {
    node?.children?.forEach((child) => {
      const childId = getNodeKey(child);
      if (childId) childIds.add(childId);
    });
  });

  return taxonomies.filter((node) => {
    const nodeId = getNodeKey(node);
    if (childIds.has(nodeId)) return false;
    return !node.parents || node.parents.length === 0;
  });
};

const getMaximumHitCount = (nodes) =>
  nodes.reduce((max, node) => Math.max(max, toNumber(node?.hitCount), getMaximumHitCount(node?.children || [])), 0);

/**
 * Render the taxonomy name with the search match wrapped in a semantic <mark>.
 * Tokenization is pure (see @Utils/highlightMatch); the React elements are
 * created here, in the presentation layer (spec AR-004, Slice 4).
 */
function HighlightedName({ name, query }) {
  const tokens = highlightMatch(name, query);
  if (!tokens.length) return null;
  return (
    <span data-e2e="taxonomy-label" className="taxonomyLeydenTree__name">
      {tokens.map((token, index) =>
        token.matched ? (
          // eslint-disable-next-line react/no-array-index-key
          <mark key={index}>{token.text}</mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={index}>{token.text}</React.Fragment>
        )
      )}
    </span>
  );
}

HighlightedName.propTypes = {
  name: PropTypes.string,
  query: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

/**
 * When the search query matched a synonym rather than the displayed name, show
 * the matched synonym visibly in parentheses (italic, highlighted) — mirroring
 * the legacy TreeViewListItem treatment.
 */
function SynonymMatchHint({ node }) {
  const synonyms = Array.isArray(node.matchedSynonyms) ? node.matchedSynonyms : [];
  if (!synonyms.length) return null;
  return (
    <>
      {' '}
      <span className="taxonomyLeydenTree__synonym" data-e2e="taxonomy-hit-synonym">
        (<mark>{synonyms[0]}</mark>)
      </span>
    </>
  );
}

SynonymMatchHint.propTypes = { node: PropTypes.object.isRequired };

/**
 * Hit-count treatment: a proportional bar plus the numeric value as text (Figma).
 *
 * Entirely `aria-hidden`. The count is announced as part of the row checkbox's
 * accessible name instead (see `CountForLabel`).
 */
function Count({ hitCount, maxCount, showIndicator }) {
  const text = formatCount(hitCount);
  if (text == null) return null;
  const count = Math.max(toNumber(hitCount), 0);
  const progressMaximum = Math.max(maxCount, 1);
  const percent = `${Math.min((count / progressMaximum) * 100, 100).toFixed(1)}%`;
  return (
    <span className="taxonomyLeydenTree__count" aria-hidden="true">
      {showIndicator && (
        <span className="taxonomyLeydenTree__bar">
          <span className="taxonomyLeydenTree__bar_fill" style={{ width: percent }} />
        </span>
      )}
      <span className="taxonomyLeydenTree__count_value" data-testid="taxonomy-count-badge">
        {text}
      </span>
    </span>
  );
}

Count.propTypes = {
  hitCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxCount: PropTypes.number,
  showIndicator: PropTypes.bool,
};

/** The hit count as visually hidden text inside the checkbox label. */
function CountForLabel({ hitCount }) {
  const text = formatCount(hitCount);
  if (text == null) return null;
  return <span className="u-els-hide-visually">{` ${text} ${COUNT_UNIT_LABEL}`}</span>;
}

CountForLabel.propTypes = {
  hitCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

/**
 * Semantic taxonomy tree.
 *
 * The installed Leyden Tree wraps every branch label in one link button. That
 * label contains both a checkbox and a hit count, which creates nested controls
 * and makes Leyden's hover border span the entire row. The tree is rendered here
 * so expansion, selection, and the count are separate controls/content while
 * retaining Leyden's ButtonWithIcon and Checkbox components.
 */
function TaxonomyLeydenTree({ taxonomies = [], query = '', onSelect, onLoad, ariaLabel, id, showCountIndicator = true }) {
  const sectionRef = useRef(null);
  const requestedRef = useRef(new Set());
  // Node objects are regenerated by selection and lazy-load updates. Keep user
  // choices keyed by stable branch IDs so those updates cannot reset them.
  const [expansionOverrides, setExpansionOverrides] = useState(() => new Map());

  const rootTaxonomies = useMemo(() => getRootTaxonomies(taxonomies), [taxonomies]);
  const maxCount = useMemo(() => getMaximumHitCount(taxonomies), [taxonomies]);

  const handleToggle = useCallback(
    (node, branchId, isExpanded) => {
      const nextExpanded = !isExpanded;
      setExpansionOverrides((current) => {
        const next = new Map(current);
        next.set(branchId, nextExpanded);
        return next;
      });

      const hasUnloadedChildren = (node.hasChildren || node.childrenCount > 0) && (!node.children || node.children.length === 0);
      if (nextExpanded && hasUnloadedChildren && !requestedRef.current.has(branchId)) {
        requestedRef.current.add(branchId);
        onLoad(node);
      }
    },
    [onLoad]
  );

  const renderCheckbox = useCallback(
    (node, branchId) => {
      const selected = node.state === TAXOMONY_STATES.SELECTED;
      const indeterminate = node.state === TAXOMONY_STATES.HALFSELECTED;

      return (
        <span className="taxonomyLeydenTree__checkbox" role="presentation">
          <Checkbox
            id={`${branchId}-select`}
            data-e2e="tree-view-list-item-select"
            data-testid="taxonomy-select"
            indeterminate={indeterminate}
            checked={selected}
            onChange={() => onSelect(node)}
            label={
              <>
                <HighlightedName name={node.name} query={query} />
                <SynonymMatchHint node={node} />
                <CountForLabel hitCount={node.hitCount} />
              </>
            }
          />
        </span>
      );
    },
    [onSelect, query]
  );

  const renderNodes = useCallback(
    (nodes) =>
      nodes.map((node) => {
        const branchId = `${ID_PREFIX}-${node.id}`;
        const isBranch = hasNestedChildren(node);
        const isExpanded = expansionOverrides.get(branchId) ?? false;
        const row = (
          <span className="taxonomyLeydenTree__row">
            <span className="taxonomyLeydenTree__content">{renderCheckbox(node, branchId)}</span>
            <Count hitCount={node.hitCount} maxCount={maxCount} showIndicator={showCountIndicator} />
          </span>
        );

        if (!isBranch) {
          return (
            <li
              key={branchId}
              role="treeitem"
              aria-selected={node.state === TAXOMONY_STATES.SELECTED}
              className="c-els-tree__leaf"
              data-testid="mock-tree-leaf">
              {row}
            </li>
          );
        }

        const groupId = `${branchId}-group`;
        const toggle = (
          <ButtonWithIcon
            id={`${branchId}-button`}
            data-e2e="tree-view-list-item-toggle"
            className="taxonomyLeydenTree__toggle"
            sprite={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            linkIconSize="xs"
            iconOnly
            type="link"
            aria-label={node.name}
            aria-expanded={isExpanded}
            aria-controls={groupId}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleToggle(node, branchId, isExpanded);
            }}>
            {node.name}
          </ButtonWithIcon>
        );

        return (
          <li
            key={branchId}
            role="treeitem"
            aria-selected={node.state === TAXOMONY_STATES.SELECTED}
            aria-expanded={isExpanded}
            className={`c-els-tree__branch${isExpanded ? ' c-els-tree__branch--open' : ''}`}
            data-testid="mock-tree-branch">
            <div className="taxonomyLeydenTree__branchRow">
              {toggle}
              {row}
            </div>
            {isExpanded && (
              <ul id={groupId} role="group">
                {renderNodes(node.children || [])}
              </ul>
            )}
          </li>
        );
      }),
    [expansionOverrides, handleToggle, maxCount, renderCheckbox, showCountIndicator]
  );

  // Keyboard navigation remains on the interactive controls, not on the
  // treeitem itself. ArrowRight/ArrowLeft expand or collapse its branch.
  const handleKeyDown = useCallback((event) => {
    const region = sectionRef.current;
    if (!region) return;

    const focusables = Array.from(region.querySelectorAll('.taxonomyLeydenTree__toggle, .taxonomyLeydenTree__checkbox input')).filter(
      (element) => !element.disabled
    );
    if (!focusables.length) return;

    const active = document.activeElement;
    const currentIndex = focusables.indexOf(active);
    const focusAt = (index) => {
      const clamped = Math.max(0, Math.min(index, focusables.length - 1));
      focusables[clamped].focus();
    };

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusAt(focusables.length - 1);
        break;
      case 'ArrowRight':
      case 'ArrowLeft': {
        const treeItem = active?.closest?.('[role="treeitem"]');
        const toggle = treeItem?.querySelector(':scope > .taxonomyLeydenTree__branchRow .taxonomyLeydenTree__toggle');
        if (!toggle) return;
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        if ((event.key === 'ArrowRight' && !expanded) || (event.key === 'ArrowLeft' && expanded)) {
          event.preventDefault();
          toggle.click();
        }
        break;
      }
      default:
        break;
    }
  }, []);

  if (!rootTaxonomies.length) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- delegate tree keyboard navigation to the region.
    <section
      ref={sectionRef}
      className="taxonomyLeydenTree"
      aria-label={ariaLabel || 'Taxonomy lookup'}
      data-testid="taxonomy-leyden-tree"
      onKeyDown={handleKeyDown}>
      <div className="taxonomyLeydenTree__scroll">
        <ol id={id} className="c-els-tree taxonomyLeydenTree__tree" role="tree">
          {renderNodes(rootTaxonomies)}
        </ol>
      </div>
    </section>
  );
}

TaxonomyLeydenTree.propTypes = {
  taxonomies: PropTypes.array,
  query: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onSelect: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  id: PropTypes.string,
  showCountIndicator: PropTypes.bool,
};

export default TaxonomyLeydenTree;
